import axios, { AxiosError, AxiosResponse, InternalAxiosRequestConfig, isAxiosError } from 'axios';
import { baseURL } from '~/utils/constants/env';
import { StorageEnum } from '~/utils/enum';
import { TIME_OUT } from '~/utils/constants/common';
import { getTimezone } from '~/utils/helper';
import { HTTP_RESPONSE } from '~/utils/constants/http-response';
import { adminRouteAbsolute } from '~/utils/constants/route';

const axiosClient = axios.create({
  baseURL: baseURL,
  headers: {
    Accept: 'application/json',
    'Content-Type': 'application/json',
  },
  timeout: TIME_OUT,
});

// Add a request interceptor
axiosClient.interceptors.request.use(
  function (config: InternalAxiosRequestConfig) {
    // Do something before request is sent
    const token = localStorage.getItem(StorageEnum.ACCESS_TOKEN);

    const publicApis = [
      '/api/auth/login',
      '/api/auth/forgot-password',
      '/api/auth/reset-password',
      '/api/job-invoices/public/', // customer invoice link (token, no login)
    ];

    const isPublicApi = publicApis.some(api => config.url?.includes(api));

    if (!isPublicApi && !token) {
      window.location.href = adminRouteAbsolute.login;

      return Promise.reject(new Error('No token available'));
    }

    config.headers.Authorization = token ? `Bearer ${token}` : '';
    config.headers['Bor-Timezone'] = getTimezone();
    return config;
  },

  function (error: AxiosError | Error) {
    // Do something with request error
    return Promise.reject(error);
  }
);

// User-facing text for a failed request. The BE already returns safe messages ({ message });
// this covers responses without one (proxy/nginx pages, timeouts, network down).
const STATUS_MESSAGES: Record<number, string> = {
  400: 'The request is invalid. Please check the entered data.',
  401: 'Your session has expired. Please log in again.',
  403: "You don't have permission to perform this action.",
  404: 'The requested data was not found.',
  413: 'The uploaded data is too large.',
  429: 'Too many requests. Please wait a moment and try again.',
};

export const getFriendlyErrorMessage = (error: AxiosError<any>): string => {
  const serverMessage = error.response?.data?.message;
  if (typeof serverMessage === 'string' && serverMessage.trim()) return serverMessage;
  const status = error.response?.status;
  if (!status) {
    return error.code === 'ECONNABORTED'
      ? 'The server took too long to respond. Please try again.'
      : 'Cannot connect to the server. Please check your internet connection.';
  }
  return STATUS_MESSAGES[status] ?? (status >= 500 ? 'The server encountered an error. Please try again later.' : 'Something went wrong. Please try again.');
};

// Add a response interceptor
axiosClient.interceptors.response.use(
  function (response: AxiosResponse) {
    // Any status code that lie within the range of 2xx cause this function to trigger
    // Do something with response data
    return response;
  },

  function (error: AxiosError | Error) {
    // Do something with response error
    if (isAxiosError(error)) {
      // Handle Unauthorize case
      if (error?.response?.status === HTTP_RESPONSE.COMMON.UNAUTHORIZED.code) {
        localStorage.removeItem(StorageEnum.ACCESS_TOKEN);
        localStorage.removeItem(StorageEnum.REFRESH_TOKEN);

        setTimeout(() => {
          window.location.href = adminRouteAbsolute.login;
        }, 50);
      }

      // Normalize once here so every caller shows a readable message, whether it reads
      // `error.message` or `error.response.data.message` (never "Request failed with status code 403").
      const message = getFriendlyErrorMessage(error);
      error.message = message;
      if (error.response) {
        const data = error.response.data;
        error.response.data = data && typeof data === 'object' ? { ...data, message } : { message };
      } else {
        // No response (network/timeout): many callers read error.response.data.message directly -> avoid a crash.
        (error as any).response = { status: 0, data: { message } };
      }
    }

    return Promise.reject(error);
  }
);

export default axiosClient;
